import React, { useCallback, useRef, useState } from "react";

import { CloseIcon, Sparkle2 } from "../../components/Icons";

import { BUTTON_CLASS, CLOSE_BUTTON_CLASS, EditTextArea, EditTextField } from "./BlockEditControls";
import { CARD_SURFACE_STYLE, DashboardWindow } from "./dashboardHelpers";
import { useDismissOnOutside } from "./useDismissOnOutside";
import { usePopoverPosition } from "./usePopoverPosition";

export interface DashboardHeaderEdits {
  title: string;
  description: string;
}

interface Props {
  title: string;
  description: string;
  /** Persists both fields. Only called when something actually changed. */
  onSave: (edits: DashboardHeaderEdits) => void;
  /** Hands the dashboard's header over to the chat agent instead. */
  onEditInChat: () => void;
  onClose: () => void;
  targetWindow: DashboardWindow;
}

// Matches the Figma frame (node 6908:39442 — 400 wide, height hugs the description).
// At 400 this popover sits on a looser grid than the 320-wide BlockEditPopover it was
// built from: 16px gutters and 12px between groups rather than 12 and 8. Keeping the
// block popover's tighter rhythm here left the gap above the buttons as the widest in
// the panel, which is what read as too much padding under the description.
const POPOVER_WIDTH = 400;

/**
 * Edits the dashboard's own title and description — the `<h1 id="dashboardTitle">` and
 * `<p id="dashboardDescription">` in the iframe's header — from a popover on the hover pencil
 * beside the heading.
 *
 * Both controls are text, so unlike BlockEditPopover there is nothing to apply instantly: Done
 * is the save, and dismissing discards, consistent with how that popover treats its text fields.
 *
 * Built from the shared BlockEditControls primitives rather than reusing
 * routes/Dashboard/components/RenameDashboardModal: that modal is styled-components + AntD, and
 * the dashboard header lives inside the iframe, which has no StyleSheetManager — the same
 * constraint that put ToggleSwitch here instead of components/Switch. The nav bar's more-menu
 * Rename still opens that modal; this popover is the in-page path.
 */
export const DashboardHeaderEditPopover: React.FC<Props> = ({
  title,
  description,
  onSave,
  onEditInChat,
  onClose,
  targetWindow,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { style: positionStyle } = usePopoverPosition(
    popoverRef,
    true,
    targetWindow,
    POPOVER_WIDTH
  );

  const [draft, setDraft] = useState<DashboardHeaderEdits>({ title, description });
  // Frozen at mount so the dirty check compares against what the user opened.
  const initialRef = useRef<DashboardHeaderEdits>({ title, description });

  useDismissOnOutside(popoverRef, onClose, targetWindow);

  const handleDone = useCallback(() => {
    const initial = initialRef.current;
    // Skip the write when nothing changed — it would still cost a save and an undo entry.
    if (draft.title !== initial.title || draft.description !== initial.description) {
      onSave({ title: draft.title, description: draft.description });
    }
    onClose();
  }, [draft, onSave, onClose]);

  return (
    <div
      ref={popoverRef}
      data-testid="dashboard-header-edit-popover"
      className="absolute z-50 text-card-foreground rounded-[12px] shadow-2xl border border-card-border block-fade-in pt-[8px]"
      style={{ ...positionStyle, ...CARD_SURFACE_STYLE }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-[6px] h-[24px] px-[16px]">
        <span className="text-[14px] leading-[18px] font-bold">Rename Dashboard</span>
        <button onClick={onClose} aria-label="Close" className={CLOSE_BUTTON_CLASS}>
          <CloseIcon width={10} height={10} />
        </button>
      </div>

      <div className="px-[16px] pt-[12px] pb-0 space-y-[12px]">
        <EditTextField
          label="Title"
          value={draft.title}
          onChange={value => setDraft(prev => ({ ...prev, title: value }))}
          placeholder="Enter title..."
        />

        <EditTextArea
          label="Description"
          value={draft.description}
          onChange={value => setDraft(prev => ({ ...prev, description: value }))}
          placeholder="Enter description"
        />

        <div className="flex items-center justify-between gap-[24px] pt-[8px] pb-[16px]">
          <button
            onClick={onEditInChat}
            className={`${BUTTON_CLASS} gap-[8px] text-card-foreground bg-card-text-button-hover-background hover:bg-card-foreground/15`}
          >
            <Sparkle2 width={16} height={16} />
            Edit in chat
          </button>
          <button
            onClick={handleDone}
            className={`${BUTTON_CLASS} bg-primary-button-background text-primary-button-foreground hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground`}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
